import {
  WCAG_AA_TEXT_CONTRAST,
  contrastRatio,
  imageLuminanceToForeground,
  imageLuminanceToOverlay,
  isReadablePair,
  relativeLuminance,
} from './contrastAwareThemeEngine';
import { visualAuthority } from './visualAuthority';

const imageRegionCache = new WeakMap();

const SEMANTIC_SURFACE_AUTHORITY_SELECTOR = '[data-og-foreground-authority="semantic-surface"]';
const OPAQUE_SURFACE_SELECTOR = [
  '[data-og-adaptive-surface="true"]',
  '[data-og-local-contrast-surface="true"]',
  '[data-og-component-surface]',
  '.MuiCard-root',
  '.MuiPaper-root',
].join(',');

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const findImageRegionHost = (element) => {
  if (element?.closest?.(SEMANTIC_SURFACE_AUTHORITY_SELECTOR)) {
    return null;
  }
  const host = element?.closest?.('[data-og-image-aware], .og-image-aware, [data-og-image-tone]');
  const surface = element?.closest?.(OPAQUE_SURFACE_SELECTOR);
  // Text on an opaque card/paper belongs to that surface. An image-aware
  // ancestor behind the surface must not become its foreground authority.
  if (host && surface && surface !== host && host.contains(surface)) {
    return null;
  }
  return host;
};

const findRegionImage = (host) => {
  if (!host) {
    return null;
  }
  const images = Array.from(host.querySelectorAll?.('img, .og-theme-aware-image') || []);
  return images.find((image) => image instanceof HTMLImageElement && image.complete && image.naturalWidth && image.naturalHeight) || null;
};

const renderedImageSourceRect = (image, sampleRect) => {
  const imageRect = image.getBoundingClientRect();
  const intersection = {
    left: clamp(Math.max(sampleRect.left, imageRect.left), imageRect.left, imageRect.right),
    top: clamp(Math.max(sampleRect.top, imageRect.top), imageRect.top, imageRect.bottom),
    right: clamp(Math.min(sampleRect.right, imageRect.right), imageRect.left, imageRect.right),
    bottom: clamp(Math.min(sampleRect.bottom, imageRect.bottom), imageRect.top, imageRect.bottom),
  };
  const width = intersection.right - intersection.left;
  const height = intersection.bottom - intersection.top;
  if (width <= 0 || height <= 0) {
    return null;
  }

  const computed = window.getComputedStyle(image);
  const objectFit = computed.objectFit || 'fill';
  const naturalWidth = image.naturalWidth;
  const naturalHeight = image.naturalHeight;
  const renderedWidth = imageRect.width;
  const renderedHeight = imageRect.height;
  let scaleX = naturalWidth / renderedWidth;
  let scaleY = naturalHeight / renderedHeight;
  let offsetX = 0;
  let offsetY = 0;

  if (objectFit === 'cover' || objectFit === 'contain') {
    const scale = objectFit === 'cover'
      ? Math.max(renderedWidth / naturalWidth, renderedHeight / naturalHeight)
      : Math.min(renderedWidth / naturalWidth, renderedHeight / naturalHeight);
    const paintedWidth = naturalWidth * scale;
    const paintedHeight = naturalHeight * scale;
    offsetX = (renderedWidth - paintedWidth) / 2;
    offsetY = (renderedHeight - paintedHeight) / 2;
    scaleX = 1 / scale;
    scaleY = 1 / scale;
  }

  const localLeft = intersection.left - imageRect.left - offsetX;
  const localTop = intersection.top - imageRect.top - offsetY;
  return {
    sx: clamp(localLeft * scaleX, 0, naturalWidth - 1),
    sy: clamp(localTop * scaleY, 0, naturalHeight - 1),
    sw: clamp(width * scaleX, 1, naturalWidth),
    sh: clamp(height * scaleY, 1, naturalHeight),
  };
};

export const sampleImageRegionLuminance = (image, element) => {
  if (!image || !element || typeof document === 'undefined') {
    return null;
  }
  const elementRect = element.getBoundingClientRect();
  if (!elementRect.width || !elementRect.height) {
    return null;
  }
  const sourceRect = renderedImageSourceRect(image, elementRect);
  if (!sourceRect) {
    return null;
  }
  const cached = imageRegionCache.get(image);
  let canvas = cached?.canvas;
  let context = cached?.context;
  if (!canvas || !context) {
    canvas = document.createElement('canvas');
    context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) {
      return null;
    }
    imageRegionCache.set(image, { canvas, context });
  }

  const sampleWidth = 32;
  const sampleHeight = 20;
  canvas.width = sampleWidth;
  canvas.height = sampleHeight;
  try {
    context.clearRect(0, 0, sampleWidth, sampleHeight);
    context.drawImage(
      image,
      sourceRect.sx,
      sourceRect.sy,
      sourceRect.sw,
      sourceRect.sh,
      0,
      0,
      sampleWidth,
      sampleHeight,
    );
    const { data } = context.getImageData(0, 0, sampleWidth, sampleHeight);
    let red = 0;
    let green = 0;
    let blue = 0;
    let count = 0;
    for (let index = 0; index < data.length; index += 4) {
      const alpha = data[index + 3] / 255;
      if (alpha <= 0.05) {
        continue;
      }
      red += data[index] * alpha;
      green += data[index + 1] * alpha;
      blue += data[index + 2] * alpha;
      count += alpha;
    }
    if (!count) {
      return null;
    }
    const rgb = [red / count, green / count, blue / count];
    return {
      luminance: relativeLuminance(rgb),
      background: `rgb(${Math.round(rgb[0])}, ${Math.round(rgb[1])}, ${Math.round(rgb[2])})`,
    };
  } catch (error) {
    return null;
  }
};

export const resolveImageRegionForeground = (element, options = {}) => {
  if (!element || typeof window === 'undefined') {
    return null;
  }
  const host = findImageRegionHost(element);
  const image = findRegionImage(host);
  if (!host || !image) {
    return null;
  }
  const sample = sampleImageRegionLuminance(image, element);
  if (!sample) {
    return null;
  }
  const foreground = imageLuminanceToForeground(sample.luminance);
  const minimumRatio = options.minimumRatio || WCAG_AA_TEXT_CONTRAST;
  const correctedForeground = isReadablePair(foreground, sample.background, minimumRatio)
    ? foreground
    : (contrastRatio('#ffffff', sample.background) >= contrastRatio('#0f172a', sample.background) ? '#ffffff' : '#0f172a');
  const overlay = imageLuminanceToOverlay(sample.luminance);
  return {
    background: sample.background,
    foreground: correctedForeground,
    luminance: sample.luminance,
    overlay,
    tone: sample.luminance < 0.42 ? 'dark' : sample.luminance > 0.72 ? 'light' : 'mixed',
    host,
  };
};

export const applyImageRegionForeground = (element, options = {}) => {
  const result = resolveImageRegionForeground(element, options);
  if (!result) {
    return false;
  }
  result.host.dataset.ogImageTone = result.tone;
  visualAuthority.requestStyles('ImageRegionEngine', result.host, {
    '--og-image-foreground': result.foreground,
    '--og-image-overlay': result.overlay,
  });
  visualAuthority.requestStyles('ImageRegionEngine', element, {
    '--og-context-foreground': result.foreground,
    '--og-local-foreground': result.foreground,
    '--og-local-icon': result.foreground,
    color: result.foreground,
    '-webkit-text-fill-color': result.foreground,
    'webkit-text-fill-color': result.foreground,
  });
  visualAuthority.requestStyles('ImageRegionEngine', element, { 'text-shadow': result.foreground === '#0f172a'
    ? '0 1px 2px rgba(255,255,255,0.72)'
    : '0 1px 3px rgba(0,0,0,0.68)' });
  element.dataset.ogContextForeground = 'true';
  element.dataset.ogImageRegionForeground = 'true';
  element.querySelectorAll?.('svg,.MuiSvgIcon-root,.MuiIcon-root,.fa,.fas,.far,.fab,i[class*="fa-"],.og-theme-aware-icon,.og-theme-aware-icon-asset').forEach((icon) => {
    visualAuthority.requestStyles('ImageRegionEngine', icon, {
      '--og-context-foreground': result.foreground,
      '--og-local-icon': result.foreground,
      color: 'currentColor',
    });
    icon.style.fill = 'currentColor';
    icon.style.stroke = 'currentColor';
    icon.dataset.ogThemeAwareIcon = 'true';
  });
  return true;
};

export const ImageRegionContrastEngine = {
  sampleImageRegionLuminance,
  resolveImageRegionForeground,
  applyImageRegionForeground,
};
